import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from "@angular/router"; 
import { CurrentUserService } from '../../../security/current-user.service';
import { AccessibilityHelper } from '../../../shared/accessibility.helper';
import { ScriptableBaseComponent } from '../../../shared/components/scriptable-base/scriptable-base.component';
import { SiteEditorComponent } from '../../../shared/components/site-editor/site-editor.component';
import { AlertDialogComponent, AlertDialogModel } from '../../../shared/dialogs/alert/alert-dialog.component';
import { GetSiteEtlMetadataDialog } from '../../../shared/dialogs/get-site-etl-metadata/get-site-etl-metadata.dialog';
import { ImportDataFromExcelDialog, ImportDataFromExcelDialogConfig } from '../../../shared/dialogs/import-data/import-data-from-excel.dialog';
import { IRefreshData } from '../../../shared/interfaces/irefresh-data.interface';
import { INameToPrettyNameMap } from '../../../shared/models/component-scripting/additional-element-info.model';
import { DataCollectionMilestone } from '../../../shared/models/milestones/data-collection-milestone.model';
import { Role } from '../../../shared/models/role.model';
import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { ImportDataFromExcelDialogAction } from '../../../shared/services/excel-import.service';
import { ProgressConfig } from '../../../shared/services/progress-indicator.service';
import { RoleService } from '../../../shared/services/role.service';
import { ConfigureStructureFields } from '../../../shared/models/structure/configure-structure-fields.model';
import { ConfigureStructureFieldsDialog } from '../../../shared/dialogs/configure-structure-fields/configure-structure-fields.dialog';
import { ExportFormDataDialog, ExportFormDataDialogAction, ExportFormDataDialogConfig } from '../../../shared/dialogs/export-form-data/export-form-data.dialog';

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    standalone: false
})
export class SummaryComponent extends ScriptableBaseComponent implements OnInit, AfterViewInit {
    // Properties.
    private selectedCard: string;
    private changeWorkflow: boolean;
    private roles: Role[];
    private showRolesViewer: boolean = false;
    private showSiteEditor: boolean = false;
    private showMilestonesViewer: boolean = false;
    private rootFolderId: number;

    @ViewChild('editorDrawer') drawer: MatDrawer;
    @ViewChild(SiteEditorComponent) siteEditor: SiteEditorComponent;
    @ViewChild('siteMilestones') siteMilestonesComponent: IRefreshData;

    // Constructor.
    public constructor(
        private siteService: DataCollectionService,
        private currentSiteService: CurrentSiteService,
        private currentUserService: CurrentUserService,
        private roleService: RoleService,
        private dialog: MatDialog, 
        private route: ActivatedRoute,
        private readonly titleService: Title,
        //componentHierarchyService: ComponentHierarchyService,
        elForBaseClass: ElementRef)
    {
        //super(componentHierarchyService, elForBaseClass);
        super(elForBaseClass);
        let siteWorkflowButtonNamesMap: INameToPrettyNameMap =
        {
            'Setup toc': 'Setup',
            'Active pool': 'Active',
            'cancel_presentation': 'Closed'
        };
        this.element('#siteWorkflowButtons').hasButtons().withNameToPrettyNameMap(siteWorkflowButtonNamesMap);
        let betaFeaturesButtonNamesMap: INameToPrettyNameMap =
        {
            'Disable': 'Disable Beta Features',
            'Disabled': 'Disable Beta Features',
            'Enable': 'Enable Beta Features',
            'Enabled': 'Enable Beta Features'
        };
        this.element('#siteBetaFeaturesButtons').hasButtons().withNameToPrettyNameMap(betaFeaturesButtonNamesMap);
    }

    // Life cycle methods.
    public ngOnInit() {
        super.ngOnInit();

        this.rootFolderId = this.Site.folders[0].id; 
        AccessibilityHelper.focusOnMainContent();
        this.titleService.setTitle(this.route.snapshot.data['title'] + ' for ' + this.Site.name);
    }

    public ngAfterViewInit(): void {
        return;
    }

    // Methods called by my HTML code.
    public get Site(): DataCollection {
        return this.currentSiteService.Site;
    }

    public get SelectedCard(): string {
        return this.selectedCard;
    }

    public get ChangeWorkflow(): boolean {
        return this.changeWorkflow;
    }

    public get Roles(): Role[] {
        return this.roles;
    }

    public get ShowRolesViewer(): boolean {
        return this.showRolesViewer;
    }
    public set ShowRolesViewer(show: boolean) {
        this.showRolesViewer = show;
    }
    public get ShowSiteEditor(): boolean {
        return this.showSiteEditor;
    }
    public set ShowSiteEditor(show: boolean) {
        this.showSiteEditor = show;
    }
    public get ShowMilestonesViewer(): boolean {
        return this.showMilestonesViewer;
    }
    public set ShowMilestonesViewer(show: boolean) {
        this.showMilestonesViewer = show;
    }

    public get RootFolderId(): number {
        return this.rootFolderId;
    }

    public get AlphaFeaturesEnabled(): boolean {
        return this.currentSiteService.Site.betaFeaturesEnabled && this.currentUserService.user.isSystemAdmin;
    }
    public get BetaFeaturesEnabled(): boolean {
        return this.currentSiteService.Site.betaFeaturesEnabled;
    }
    public get UserIsSystemAdmin(): boolean {
        return this.currentUserService.user.isSystemAdmin;
    }

    public get UseSimplifiedPermissionsModel(): boolean {
        return this.currentSiteService.Site.useSimplifiedPermissionsModel;
    }

    // Handle control actions.
    public openEditor(card) {
        this.selectedCard = card;
        this.showSiteEditor = true;
        this.showRolesViewer = false;
        AccessibilityHelper.setFocus('#drawer-keyboard-focus-div');
    }

    public openRolesViewer() {
        this.showRolesViewer = true;
        this.showSiteEditor = false;
        this.roleService.getFolderRoles(this.rootFolderId).then(response => {
            this.roles = response.roles;
            AccessibilityHelper.setFocus('#drawer-keyboard-focus-div');
        });
    }

    public openMilestones(milestones: DataCollectionMilestone[]): void {
        this.showMilestonesViewer = true;
        this.showRolesViewer = false;
        this.showSiteEditor = false;
    }

    public closeDrawer() {
        if (this.showSiteEditor) {
            if (!this.siteEditor.formIsDirty()) {
                this.showSiteEditor = false;
                return true;
            } else {
                this.dialog.open(AlertDialogComponent, {
                    data: new AlertDialogModel("Unsaved Changes", "You have unsaved changes in the site editor.", null)
                });
                return false;
            }
        } else if (this.showMilestonesViewer) {
            if (this.siteMilestonesComponent != null)
                this.siteMilestonesComponent.refreshData();
        }

        this.showRolesViewer = false;
        this.showMilestonesViewer = false;
    }

    public doUpdate(site): void {
        // the site param is not guaranteed to be fully formed since it is passed
        // from a reactive form which doesn't include all properties
        // so make sure it fully formed
        this.showSiteEditor = false;
        this.Site.currentStatus = this.Site.currentStatus
        this.Site.statuses = this.Site.statuses;
        this.currentSiteService.Site = site
    }

    // Since site-level workflow status is really the Root Folder's workflow status
    // this performs mapping of site --> folder --> site and uses the FolderServer
    // to do the update
    public transitionWorkFlow(site: DataCollection): void {
        let rootFolder: Folder = site.folders[0];
        rootFolder.currentWorkflowState = site.currentStatus;
        rootFolder.selectedWorkflowTransition = site.selectedWorkflowTransition;
        this.siteService.transitionWorkflow(
            rootFolder,
            new ProgressConfig("Updating Site Status", "Updated Site Status")).then(res => {
                //this.currentSiteService.Site = res;
        });
    }
    /**
     * Used to enable and disable beta features via a ui control on the Summary Overview page
     * @param site
     */
    public enableDisableBetaFeatures(site: DataCollection): void {
        this.siteService.enableDisableBetaFeatures(site.id, site.betaFeaturesEnabled).then(response => {
            this.currentSiteService.Site.betaFeaturesEnabled = response.betaFeaturesEnabled;
        });
    }

    public saveSite(site): void {
        this.siteService.update(site).then(site => {
            this.currentSiteService.Site = site;
        });
    }

    public hideRolesViewer(): void {
        this.showRolesViewer = false;
    }

    public setSitePermissionsModel(site: DataCollection): void {
        this.siteService.setPermissionsModelFlag(site.id, site.useSimplifiedPermissionsModel).then(response => {
            this.currentSiteService.Site.useSimplifiedPermissionsModel = response.useSimplifiedPermissionsModel;
        });
    }

    public configureStructureFields(): void {
        let configureStructureFields: ConfigureStructureFields = null;
        if ((this.currentSiteService.Site.hideStructureMetadataFieldsJsonConfig != null) && (this.currentSiteService.Site.hideStructureMetadataFieldsJsonConfig.trim() != '')) {
            // Parse the existing configuration.
            configureStructureFields = ConfigureStructureFields.parseFromJson(this.currentSiteService.Site.hideStructureMetadataFieldsJsonConfig);
        } else {
            // Create an empty configuration.
            configureStructureFields = ConfigureStructureFields.createEmptyConfig();
        }

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '550px';
        dialogConfig.height = '455px';
        dialogConfig.data = configureStructureFields;

        const dialogRef = this.dialog.open(ConfigureStructureFieldsDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(updatedStructureFieldsConfig => {
            // If the configuration has been updated, save it.
            if (updatedStructureFieldsConfig != null) {
                this.currentSiteService.Site.hideStructureMetadataFieldsJsonConfig = ConfigureStructureFields.toJsonString(updatedStructureFieldsConfig);
                this.siteService.update(this.currentSiteService.Site).then(updatedSite => {
                    // Note:  there is nothing more to do here by design.
                })
            }
        });
    }

    public generateSiteETLMetadataClicked(): void {
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.height = '325px';
        dialogConfig.data = null;

        const dialogRef = this.dialog.open(GetSiteEtlMetadataDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(importedFormId => {
            // NOTHING TO DO HERE BY DESIGN.
        });
    }

    // The following was added for VNEXT-1216
    public get ImportSiteStructure(): ImportDataFromExcelDialogAction{
        return ImportDataFromExcelDialogAction.ImportSiteStructure;
    }
    public get ImportSiteFormInstances(): ImportDataFromExcelDialogAction {
        return ImportDataFromExcelDialogAction.ImportSiteFormInstances;
    }
    public get ImportFormInstanceGrids(): ImportDataFromExcelDialogAction {
        return ImportDataFromExcelDialogAction.ImportFormInstanceGrids;
    }
    public get ImportValuesToFormInstances(): ImportDataFromExcelDialogAction {
        return ImportDataFromExcelDialogAction.ImportValuesToExistingFormInstances;
    }

    public importFromExcel(action: ImportDataFromExcelDialogAction): void {
        let importConfig: ImportDataFromExcelDialogConfig = new ImportDataFromExcelDialogConfig();
        importConfig.action = action;
        importConfig.dataCollectionId = this.currentSiteService.Site.id;

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '1000px';
        dialogConfig.data = importConfig;

        const dialogRef = this.dialog.open(ImportDataFromExcelDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(ignoredAsyncJob => {
            // NOTHING TO DO HERE BY DESIGN.
        });
    }

    public exportToExcel(): void {
        let currentFolder: Folder = this.Site.getRootFolder();

        let dialogueData: ExportFormDataDialogConfig = new ExportFormDataDialogConfig(ExportFormDataDialogAction.ExportFolderFormData, currentFolder.id, currentFolder.dataCollectionId);
        dialogueData.exportOnly = true;

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.data = dialogueData;
        const dialogRef = this.dialog.open(ExportFormDataDialog, dialogConfig);
    }
}
